import React, { useEffect, useState } from 'react';

import { Skeleton, Stack, Text, VStack } from '@chakra-ui/react';

import { useSearchOutcomesEnriched } from '@/hooks/useSearchOutcomesEnriched';
import { SearchOutcomeHit, SearchOutcomesParams } from '@/types/search';

import { OutcomeResultCard } from './OutcomeResultCard';

interface SimilarJobsSectionProps {
  hits: SearchOutcomeHit[];
  queryText: string;
  isLoading?: boolean;
  onSelect: (hit: SearchOutcomeHit) => void;
  onOpenJob?: (jobId: number) => void;
  /** Optional outcomes filters forwarded to the enriched fetch. */
  filterParams?: Omit<SearchOutcomesParams, 'queryText'>;
  selectedJobIds?: number[];
  onToggleSelect?: (hit: SearchOutcomeHit) => void;
  showSelection?: boolean;
}

const SKELETON_COUNT = 3;

export const SimilarJobsSection: React.FC<SimilarJobsSectionProps> = ({
  hits,
  queryText,
  isLoading = false,
  onSelect,
  onOpenJob,
  filterParams,
  selectedJobIds,
  onToggleSelect,
  showSelection = false,
}) => {
  const [expandedJobId, setExpandedJobId] = useState<number | null>(null);

  const {
    relatedWorks,
    isLoading: isRelatedLoading,
    isFetching: isRelatedFetching,
    isError: isRelatedError,
  } = useSearchOutcomesEnriched({
    queryText,
    enabled: expandedJobId != null,
    ...filterParams,
  });

  const filterKey = [
    filterParams?.typeId,
    filterParams?.categoryId,
    filterParams?.resourceId,
    filterParams?.contactId,
    filterParams?.siteKey,
    filterParams?.startDate,
    filterParams?.endDate,
    filterParams?.limit,
  ].join('|');

  useEffect(() => {
    setExpandedJobId(null);
  }, [queryText, filterKey]);

  if (!isLoading && hits.length === 0) {
    return null;
  }

  return (
    <VStack
      align='stretch'
      gap={0}
      w='full'
      minW={0}
      data-cy='global-search-similar-jobs'
    >
      <Stack gap={0} align='stretch' minW={0} w='full'>
        <Text
          fontSize='md'
          fontWeight='semibold'
          color='tabs.font-selected'
          borderBottom='2px solid'
          borderColor='tabs.font-selected'
          w='fit-content'
          pb={2}
          mb={2}
        >
          Similar jobs
        </Text>
        <Stack gap={{ base: 2, xl: 2.5 }} w='full' minW={0} align='stretch'>
          {isLoading && hits.length === 0
            ? Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                <Skeleton
                  key={`similar-jobs-skeleton-${index}`}
                  height='112px'
                  rounded='2xl'
                  startColor='glass-items.bg-active'
                  endColor='cards.bg'
                />
              ))
            : hits.map(hit => (
                <OutcomeResultCard
                  key={`outcome:${hit.jobId}`}
                  hit={hit}
                  onSelect={onSelect}
                  isExpanded={expandedJobId === hit.jobId}
                  onExpandedChange={expanded => {
                    setExpandedJobId(expanded ? hit.jobId : null);
                  }}
                  relatedWorks={relatedWorks}
                  isRelatedLoading={isRelatedLoading || isRelatedFetching}
                  isRelatedError={isRelatedError}
                  outcomeHits={hits}
                  onOpenRelatedJob={onOpenJob}
                  showSelection={showSelection}
                  isSelected={selectedJobIds?.includes(hit.jobId)}
                  onToggleSelect={onToggleSelect}
                />
              ))}
        </Stack>
      </Stack>
    </VStack>
  );
};
